import React, { useState, useEffect } from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING, EMZI_TYPOGRAPHY } from '../../styles/emziCI';
import axios from 'axios';
import { txt } from '../../utils/bilingualText';
import { BACKEND_URL } from '../../utils/backendUrl';


const ActivePacksPanel = ({ studioType = 'content', productIds = [], campaignId = null, onPacksLoaded = null }) => {
  const [activePacks, setActivePacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPack, setExpandedPack] = useState(null);

  useEffect(() => {
    fetchActivePacks();
  }, [studioType, productIds, campaignId]);

  const fetchActivePacks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/v3/studios/active-packs`, {
        studio_type: studioType,
        product_ids: productIds,
        campaign_id: campaignId
      });
      
      if (response.data.status === 'success') {
        const packs = response.data.active_packs || [];
        setActivePacks(packs);
        
        // Callback to parent component
        if (onPacksLoaded && typeof onPacksLoaded === 'function') {
          onPacksLoaded(packs);
        }
      }
    } catch (err) {
      console.error('Error fetching active packs:', err);
      setError('Failed to load active packs');
    } finally {
      setLoading(false);
    }
  };

  const getPackIcon = (packType) => {
    const icons = {
      product: '🧃',
      promotion: '🎯',
      values: '💎',
      compliance: '⚖️',
      direction: '🧭',
      services: '🏥',
      ambassador: '🌟',
      health: '💚',
      viral: '🔥',
      trend: '📈',
    };
    return icons[packType] || '📦';
  };

  const getPackColor = (packType) => {
    const colors = {
      product: '#3B82F6',
      promotion: '#F59E0B',
      values: '#8B5CF6',
      compliance: '#EF4444',
      direction: '#10B981',
      services: '#06B6D4',
      ambassador: '#EC4899',
      health: '#14B8A6',
      viral: '#F97316',
      trend: '#6366F1',
    };
    return colors[packType] || EMZI_COLORS.primary;
  };

  if (loading) {
    return (
      <div style={{ padding: EMZI_SPACING.md, textAlign: 'center', color: EMZI_COLORS.textMuted }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
        <div style={{ fontSize: '12px' }}>Loading active packs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: EMZI_SPACING.md }}>
        <div style={{
          padding: EMZI_SPACING.sm,
          backgroundColor: EMZI_COLORS.error + '20',
          color: EMZI_COLORS.error,
          borderRadius: EMZI_RADIUS.md,
          fontSize: '12px'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: EMZI_COLORS.background
    }}>
      {/* Header */}
      <div style={{ padding: EMZI_SPACING.md, borderBottom: `1px solid ${EMZI_COLORS.border}` }}>
        <h3 style={{ ...EMZI_TYPOGRAPHY.h3, margin: 0, marginBottom: '4px' }}>
          📚 Active Knowledge Packs
        </h3>
        <p style={{ fontSize: '11px', color: EMZI_COLORS.textMuted, margin: 0 }}>
          {activePacks.length} pack{activePacks.length !== 1 ? 's' : ''} active for this session
        </p>
      </div>

      {/* Packs List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: EMZI_SPACING.sm }}>
        {activePacks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: EMZI_SPACING.lg, color: EMZI_COLORS.textMuted }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
            <div style={{ fontSize: '12px' }}>No active packs for this session</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.sm }}>
            {activePacks.map((pack, index) => (
              <PackCard
                key={pack.pack_id || index}
                pack={pack}
                icon={getPackIcon(pack.pack_type)}
                color={getPackColor(pack.pack_type)}
                isExpanded={expandedPack === pack.pack_id}
                onToggle={() => setExpandedPack(expandedPack === pack.pack_id ? null : pack.pack_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PackCard = ({ pack, icon, color, isExpanded, onToggle }) => {
  const content = pack.content || {};
  const aiContext = pack.ai_context || {};
  const complianceProfile = pack.compliance_profile || {};

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        border: `1px solid ${color}20`,
        borderLeft: `4px solid ${color}`,
        borderRadius: EMZI_RADIUS.md,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onClick={onToggle}
    >
      {/* Pack Header */}
      <div style={{ padding: EMZI_SPACING.sm, display: 'flex', alignItems: 'start', gap: '8px' }}>
        <div style={{ fontSize: '20px', lineHeight: 1 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: EMZI_COLORS.textPrimary, marginBottom: '2px' }}>
            {txt(pack.name, 'en', 'Untitled Pack')}
          </div>
          <div style={{ fontSize: '10px', color: color, textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>
            {pack.pack_type}
          </div>
          {txt(pack.description) && (
            <div style={{ fontSize: '10px', color: EMZI_COLORS.textSecondary, lineHeight: '1.4' }}>
              {txt(pack.description).substring(0, 80)}...
            </div>
          )}
        </div>
        <div style={{ fontSize: '16px', color: EMZI_COLORS.textMuted, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          ▼
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ padding: EMZI_SPACING.sm, paddingTop: 0, borderTop: `1px solid ${EMZI_COLORS.border}` }}>
          {/* Tone Guidelines */}
          {aiContext.tone_influence && (
            <div style={{ marginBottom: EMZI_SPACING.xs }}>
              <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', color: EMZI_COLORS.textMuted, marginBottom: '4px' }}>
                Tone Guidelines
              </div>
              <div style={{ fontSize: '11px', color: EMZI_COLORS.textSecondary }}>
                Influence: {Math.round(aiContext.tone_influence * 100)}%
              </div>
            </div>
          )}

          {/* Key Themes */}
          {content.key_features && content.key_features.length > 0 && (
            <div style={{ marginBottom: EMZI_SPACING.xs }}>
              <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', color: EMZI_COLORS.textMuted, marginBottom: '4px' }}>
                Key Themes
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {content.key_features.slice(0, 3).map((feature, idx) => (
                  <span key={`item-${idx}`} style={{
                    padding: '2px 6px',
                    backgroundColor: color + '20',
                    color: color,
                    borderRadius: EMZI_RADIUS.sm,
                    fontSize: '9px',
                    fontWeight: '600'
                  }}>
                    {typeof feature === 'object' ? feature.en : feature}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Safe Claims */}
          {(content.approved_claims || content.benefits) && (
            <div style={{ marginBottom: EMZI_SPACING.xs }}>
              <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', color: EMZI_COLORS.success, marginBottom: '4px' }}>
                ✅ Safe Claims
              </div>
              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '10px', color: EMZI_COLORS.textSecondary }}>
                {(content.approved_claims || content.benefits || []).slice(0, 2).map((claim, idx) => (
                  <li key={`item-${idx}`} style={{ marginBottom: '2px' }}>
                    {typeof claim === 'object' ? claim.en : claim}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Prohibited Claims (Compliance/Health packs) */}
          {content.prohibited_claims && content.prohibited_claims.length > 0 && (
            <div>
              <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', color: EMZI_COLORS.error, marginBottom: '4px' }}>
                ⛔ Avoid These
              </div>
              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '10px', color: EMZI_COLORS.error }}>
                {content.prohibited_claims.slice(0, 2).map((claim, idx) => (
                  <li key={`item-${idx}`} style={{ marginBottom: '2px' }}>
                    {typeof claim === 'object' ? claim.en : claim}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivePacksPanel;
